// ===================================================================================================
// PackagesFCL.jsx — Minimal, controlled FCL packages editor (memoized)
// - Stateless: receives `items` and emits patches via `setItems`
// - Max rows enforced by `maxRows` (defaults to 3)
// - Read-only mode renders values as disabled inputs
// ===================================================================================================

import React, { useMemo, useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { MInputTextField, MButton } from "src/M_Components";
import { useTranslation } from "react-i18next";
import DynamicSelectBox from "../Forms/DynamicSelectBox";

// RHF imports (optional usage)
import { useFormContext, Controller } from "react-hook-form";

import "./Packages.css"; // shared responsive layout for Packages* components

/** Safely access RHF context (component still works without FormProvider) */
function useOptionalFormContext() {
  try {
    // NOTE: react-hook-form throws if not inside a FormProvider; swallow and return null.
    return useFormContext();
  } catch {
    return null;
  }
}

function PackagesFCLInner({ items, setItems, maxRows = 3, readOnly = false }) {
  const { t } = useTranslation();
  const rhf = useOptionalFormContext();

  // -------------------------------------------------------------------------------------------------
  // RHF bootstrapping: seed fclItems once (from legacy items).
  // IMPORTANT: we do NOT call setValue during render; only here or in handlers.
  // -------------------------------------------------------------------------------------------------
  useEffect(() => {
    if (!rhf) return;
    const current = rhf.getValues("fclItems");
    if (!Array.isArray(current) || current.length === 0) {
      rhf.setValue("fclItems", Array.isArray(items) ? items : [], {
        shouldDirty: false,
        shouldTouch: false,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rhf]);

  /** Rows to render:
   *  - When RHF exists, prefer current form values OR fallback to legacy items.
   *  - We do NOT use watch() here to avoid remount loops that can block typing.
   */
  const rows = useMemo(() => {
    if (rhf) {
      const gv = rhf.getValues("fclItems");
      if (Array.isArray(gv)) return gv;
    }
    return items || [];
  }, [rhf, items]);

  // -------------------------------------------------------------------------------------------------
  // BUFFER LOCAL PARA NUMÉRICOS (evita “bounce” com RHF enquanto se escreve)
  // - Mantém qty/weight como strings controladas por input.
  // - Sincroniza com RHF/parent apenas no onBlur.
  // -------------------------------------------------------------------------------------------------
  const [buf, setBuf] = useState([]);

  // Hidratar buffer a partir de rows quando o comprimento muda (não sobrescreve enquanto o user digita)
  useEffect(() => {
    setBuf((prev) => {
      const next = [...(prev || [])];
      for (let i = 0; i < rows.length; i += 1) {
        const r = rows[i] || {};
        if (!next[i]) {
          next[i] = {
            quantity: r.quantity ?? "",
            weightKg: r.weightKg ?? "",
          };
        }
      }
      return next.slice(0, rows.length);
    });
  }, [rows.length]); // intencional: só reage a add/remove de linhas

  /** Replace entire array in RHF + mirror to legacy setter (keeps pages that persist state unchanged) */
  const replaceAllRows = useCallback(
    (next) => {
      if (rhf) {
        rhf.setValue("fclItems", next, { shouldDirty: true });
      }
      if (typeof setItems === "function") setItems(next);
    },
    [rhf, setItems]
  );

  // Patch a single row by index (RHF-first; legacy fallback)
  const updateRow = useCallback(
    (index, patch, { emit = false } = {}) => {
      const base = rhf ? rhf.getValues("fclItems") || [] : items || [];
      const next = [...base];
      next[index] = { ...(next[index] || {}), ...patch };
      if (rhf) rhf.setValue("fclItems", next, { shouldDirty: true });
      if (emit && typeof setItems === "function") setItems(next);
    },
    [rhf, setItems, items]
  );

  // Remove row by index
  const removeRow = useCallback(
    (index) => {
      if (readOnly) return;
      const base = rhf ? rhf.getValues("fclItems") || [] : items || [];
      const next = [...base];
      next.splice(index, 1);
      replaceAllRows(next);
      // manter buffer consistente
      setBuf((b) => {
        const nb = [...(b || [])];
        nb.splice(index, 1);
        return nb;
      });
    },
    [readOnly, rhf, items, replaceAllRows]
  );

  // Add a new empty row (capped by maxRows)
  const addRow = useCallback(() => {
    if (readOnly) return;
    const empty = { cargofiveEquipmentId: null, quantity: "", weightKg: "" };
    const count = rows.length;
    if (count >= maxRows) return;

    const base = rhf ? rhf.getValues("fclItems") || [] : items || [];
    const next = [...base, empty];
    replaceAllRows(next);
    // inicializar buffer dessa linha
    setBuf((b) => {
      const nb = [...(b || [])];
      nb[next.length - 1] = { quantity: "", weightKg: "" };
      return nb;
    });
  }, [rows.length, maxRows, rhf, items, replaceAllRows, readOnly]);

  return (
    <div className="packages-fcl-root">
      {/* Empty-state helper */}
      {rows.length === 0 ? (
        <div className="packages-fcl-empty">{t("NoRowsAddedYet")}</div>
      ) : (
        rows.map((row, idx) => {
          const key = idx;

          return (
            <div key={key} className="packages-fcl-row">
              {/* Read-only fallback: mostrar ISO se não houver Id resolvido */}
              {readOnly && !row?.cargofiveEquipmentId && row?.displayIso ? (
                <MInputTextField
                  label={t("Equipments")}
                  value={row.displayIso}
                  disabled
                />
              ) : (
                <>
                  {/* Select is discrete -> emit immediately on change */}
                  {rhf ? (
                    <Controller
                      name={`fclItems.${idx}.cargofiveEquipmentId`}
                      control={rhf.control}
                      defaultValue={row?.cargofiveEquipmentId ?? null}
                      shouldUnregister={false}
                      render={({ field }) => (
                        <DynamicSelectBox
                          label={t("Equipments")}
                          tableName="CargofiveEquipments"
                          groupBy="Name"
                          valueId={field.value ?? null}
                          disabled={readOnly}
                          onChange={(sel) => {
                            const nextId = sel?.value ?? null;
                            if (nextId != null) {
                              const all = rhf.getValues("fclItems") || [];
                              const dup = all.some(
                                (r, i) =>
                                  i !== idx &&
                                  Number(r?.cargofiveEquipmentId) ===
                                    Number(nextId)
                              );
                              if (dup) return;
                            }
                            field.onChange(nextId);
                            updateRow(
                              idx,
                              { cargofiveEquipmentId: nextId },
                              { emit: true }
                            );
                          }}
                          buildFilter={({ q, groupBy, staticFilter }) => {
                            const clauses = [];
                            if (q)
                              clauses.push(
                                `contains(${groupBy},'${String(q).replace(
                                  /'/g,
                                  "''"
                                )}')`
                              );
                            if (staticFilter) clauses.push(staticFilter);

                            const takenIdsExceptMe = (
                              rhf.getValues("fclItems") || []
                            )
                              .map((r, i) =>
                                i === idx ? null : r?.cargofiveEquipmentId
                              )
                              .filter((v) => v != null && v !== "")
                              .map(Number)
                              .filter(Number.isFinite);

                            takenIdsExceptMe.forEach((id) =>
                              clauses.push(`Id ne ${id}`)
                            );
                            if (clauses.length === 0) clauses.push("Id ge 0");
                            return clauses.join(" and ");
                          }}
                          clientFilterOptions={(arr) => {
                            const takenIdsExceptMe = (
                              rhf.getValues("fclItems") || []
                            )
                              .map((r, i) =>
                                i === idx ? null : r?.cargofiveEquipmentId
                              )
                              .filter((v) => v != null && v !== "")
                              .map(Number)
                              .filter(Number.isFinite);
                            if (!takenIdsExceptMe.length) return arr;
                            const deny = new Set(takenIdsExceptMe);
                            return arr.filter(
                              (o) => !deny.has(Number(o?.value))
                            );
                          }}
                        />
                      )}
                    />
                  ) : (
                    <DynamicSelectBox
                      label={t("Equipments")}
                      tableName="CargofiveEquipments"
                      groupBy="Name"
                      valueId={row?.cargofiveEquipmentId ?? null}
                      disabled={readOnly}
                      onChange={(sel) => {
                        const nextId = sel?.value ?? null;
                        if (nextId != null) {
                          const dup = (items || []).some(
                            (r, i) =>
                              i !== idx &&
                              Number(r?.cargofiveEquipmentId) === Number(nextId)
                          );
                          if (dup) return;
                        }
                        updateRow(
                          idx,
                          { cargofiveEquipmentId: nextId },
                          { emit: true }
                        );
                      }}
                      buildFilter={({ q, groupBy, staticFilter }) => {
                        const clauses = [];
                        if (q)
                          clauses.push(
                            `contains(${groupBy},'${String(q).replace(
                              /'/g,
                              "''"
                            )}')`
                          );
                        if (staticFilter) clauses.push(staticFilter);

                        const takenIdsExceptMe = (items || [])
                          .map((r, i) =>
                            i === idx ? null : r?.cargofiveEquipmentId
                          )
                          .filter((v) => v != null && v !== "")
                          .map(Number)
                          .filter(Number.isFinite);

                        takenIdsExceptMe.forEach((id) =>
                          clauses.push(`Id ne ${id}`)
                        );
                        if (clauses.length === 0) clauses.push("Id ge 0");
                        return clauses.join(" and ");
                      }}
                      clientFilterOptions={(arr) => {
                        const takenIdsExceptMe = (items || [])
                          .map((r, i) =>
                            i === idx ? null : r?.cargofiveEquipmentId
                          )
                          .filter((v) => v != null && v !== "")
                          .map(Number)
                          .filter(Number.isFinite);
                        if (!takenIdsExceptMe.length) return arr;
                        const deny = new Set(takenIdsExceptMe);
                        return arr.filter((o) => !deny.has(Number(o?.value)));
                      }}
                    />
                  )}
                </>
              )}

              {/* Weight (KG) — numeric; update buffer onChange, emit to parent/RHF onBlur. */}
              <MInputTextField
                type="text"
                inputMode="decimal"
                label={t("Weight (KG)")}
                value={buf[idx]?.weightKg ?? ""}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === "" || /^[0-9]+([.,][0-9]*)?$/.test(v)) {
                    setBuf((b) => {
                      const nb = [...(b || [])];
                      nb[idx] = { ...(nb[idx] || {}), weightKg: v };
                      return nb;
                    });
                  }
                }}
                onBlur={() => {
                  const v = buf[idx]?.weightKg ?? "";
                  updateRow(idx, { weightKg: v }, { emit: true });
                }}
                disabled={readOnly}
              />

              {/* Quantity — numeric; update buffer onChange, emit to parent/RHF onBlur. */}
              <MInputTextField
                type="text"
                inputMode="numeric"
                label={t("Quantity")}
                value={buf[idx]?.quantity ?? ""}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === "" || /^[0-9]+$/.test(v)) {
                    setBuf((b) => {
                      const nb = [...(b || [])];
                      nb[idx] = { ...(nb[idx] || {}), quantity: v };
                      return nb;
                    });
                  }
                }}
                onBlur={() => {
                  const v = buf[idx]?.quantity ?? "";
                  updateRow(idx, { quantity: v }, { emit: true });
                }}
                disabled={readOnly}
              />

              {/* Row actions */}
              <div className="packages-fcl-actions">
                {!readOnly && (
                  <MButton
                    backgroundColor="transparent"
                    borderColor="transparent"
                    onClick={() => removeRow(idx)}
                    style={{
                      width: "40px",
                      height: "40px",
                      border: "none",
                      background: "transparent",
                      boxShadow: "none",
                    }}
                    icon={<i className="pi pi-trash packages-fcl-trash-icon" />}
                  />
                )}
              </div>
            </div>
          );
        })
      )}

      {/* Add-row action (hidden when read-only or maxed out) */}
      {!readOnly && rows.length < maxRows && (
        <div className="packages-fcl-add-row-container">
          <MButton
            icon={<i className="pi pi-plus-circle packages-fcl-add-icon" />}
            label={t("Add a Row")}
            backgroundColor="transparent"
            labelColor="var(--color-black)"
            onClick={addRow}
            normal
            hoverEffect
          />
        </div>
      )}
    </div>
  );
}

// Memo: parent should keep references stable for best results (items/equipments)
const PackagesFCL = React.memo(PackagesFCLInner);
export default PackagesFCL;

PackagesFCL.propTypes = {
  items: PropTypes.array,
  setItems: PropTypes.func,
  maxRows: PropTypes.number,
  readOnly: PropTypes.bool,
};
